import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import {
  CouponStripeCoupon,
  DiscountType,
  DurationType,
  SubscriptionPlan,
  useCreateCouponStripeCouponMutation,
  useUpdateCouponStripeCouponMutation,
} from "store/apis/couponAdmin";
import * as yup from "yup";
import {
  DISCOUNT_TYPE_OPTIONS,
  DURATION_TYPE_OPTIONS,
  getErrorMessage,
} from "./couponUtils";

type FormValues = {
  stripe_coupon_id: string;
  stripe_subscription_data_id: string;
  discount_type: DiscountType;
  discount_rate: number | "";
  duration_type: DurationType;
  duration_period: number | "";
};

const validationSchema = yup.object({
  stripe_coupon_id: yup
    .string()
    .trim()
    .required("Stripe coupon ID is required"),
  stripe_subscription_data_id: yup.string().required("Plan is required"),
  discount_type: yup.string().required("Discount type is required"),
  discount_rate: yup
    .number()
    .typeError("Discount is required")
    .required("Discount is required")
    .min(0, "Discount can't be negative")
    .when("discount_type", {
      is: "PERCENT",
      then: (schema) => schema.max(100, "Percent can't exceed 100"),
    }),
  duration_type: yup.string().required("Duration is required"),
  duration_period: yup.number().when("duration_type", {
    is: "MONTHLY",
    then: (schema) =>
      schema
        .typeError("Number of months is required")
        .required("Number of months is required")
        .min(1, "Must be at least 1 month"),
    otherwise: (schema) =>
      schema.transform((value, original) =>
        original === "" ? undefined : value
      ),
  }),
});

const getPlanLabel = (plan: SubscriptionPlan) =>
  plan.amount ? `${plan.name} - $${plan.amount}` : plan.name;

type StripeCouponModalProps = {
  isOpen: boolean;
  close: () => void;
  couponId: string;
  plans: SubscriptionPlan[];
  // Edit mode when a stripe coupon is passed
  stripeCoupon?: CouponStripeCoupon;
};

export const StripeCouponModal = ({
  isOpen,
  close,
  couponId,
  plans,
  stripeCoupon,
}: StripeCouponModalProps) => {
  const { alertToast, successToast } = useToast();
  const [createStripeCoupon, { isLoading: isCreating }] =
    useCreateCouponStripeCouponMutation();
  const [updateStripeCoupon, { isLoading: isUpdating }] =
    useUpdateCouponStripeCouponMutation();
  const isEdit = Boolean(stripeCoupon);

  const planOptions = plans.map((plan) => ({
    label: getPlanLabel(plan),
    value: plan.uuid,
  }));

  const initialValues: FormValues = {
    stripe_coupon_id: stripeCoupon?.stripe_coupon_id ?? "",
    stripe_subscription_data_id: stripeCoupon?.subscription_plan.uuid ?? "",
    discount_type: stripeCoupon?.discount_type ?? "PERCENT",
    discount_rate: stripeCoupon?.discount_rate ?? "",
    duration_type: stripeCoupon?.duration_type ?? "FOREVER",
    duration_period: stripeCoupon?.duration_period ?? "",
  };

  const onSubmit = async (values: FormValues) => {
    const payload = {
      stripe_coupon_id: values.stripe_coupon_id.trim(),
      discount_type: values.discount_type,
      discount_rate: Number(values.discount_rate),
      duration_type: values.duration_type,
      duration_period:
        values.duration_type === "MONTHLY" ? Number(values.duration_period) : 0,
    };
    try {
      if (stripeCoupon) {
        await updateStripeCoupon({
          couponId,
          mappingId: stripeCoupon.uuid,
          payload,
        }).unwrap();
      } else {
        await createStripeCoupon({
          couponId,
          payload: {
            ...payload,
            stripe_subscription_data_id: values.stripe_subscription_data_id,
          },
        }).unwrap();
      }
      successToast({
        message: isEdit ? "Stripe coupon updated" : "Stripe coupon added",
      });
      close();
    } catch (err) {
      alertToast({ message: getErrorMessage(err) });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild>
        <FormikForm<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {isEdit ? "Edit Stripe coupon" : "Add Stripe coupon"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <div className="t-text-body-sm t-text-text-30">
                  Create the coupon in Stripe Dashboard → Product catalogue →
                  Coupons first, then paste its ID here. The discount details
                  below must match Stripe. These settings are just for
                  visibility; Stripe applies the actual discount.
                </div>
                <Input
                  withForm
                  name="stripe_coupon_id"
                  label="Stripe coupon ID"
                  required
                  placeholder="e.g. BKLYSD50"
                />
                <Combobox
                  withForm
                  name="stripe_subscription_data_id"
                  label="Subscription plan"
                  placeholder="Select plan"
                  required
                  isDisabled={isEdit}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  options={planOptions}
                  value={
                    planOptions.find(
                      (option) =>
                        option.value === values.stripe_subscription_data_id
                    ) ?? null
                  }
                />
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <Combobox
                    withForm
                    name="discount_type"
                    label="Discount type"
                    required
                    isClearable={false}
                    menuPortalTarget={document.body}
                    options={DISCOUNT_TYPE_OPTIONS}
                    value={
                      DISCOUNT_TYPE_OPTIONS.find(
                        (option) => option.value === values.discount_type
                      ) ?? null
                    }
                  />
                  <Input
                    withForm
                    name="discount_rate"
                    type="number"
                    min={0}
                    label={
                      values.discount_type === "PERCENT"
                        ? "Discount (%)"
                        : "Discount ($)"
                    }
                    required
                  />
                </div>
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <Combobox
                    withForm
                    name="duration_type"
                    label="Duration"
                    required
                    isClearable={false}
                    menuPortalTarget={document.body}
                    options={DURATION_TYPE_OPTIONS}
                    value={
                      DURATION_TYPE_OPTIONS.find(
                        (option) => option.value === values.duration_type
                      ) ?? null
                    }
                  />
                  {values.duration_type === "MONTHLY" && (
                    <Input
                      withForm
                      name="duration_period"
                      type="number"
                      min={1}
                      label="Number of months"
                      required
                    />
                  )}
                </div>
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating}
                >
                  {isEdit ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
